import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import supabase from '../lib/supabase';

const AuthContext = createContext(null);

const ROLE_HOME = {
  administrator: '/admin',
  project_manager: '/manager',
  site_agent: '/agent',
  client: '/client',
};

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadProfile = useCallback(async (userId) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, full_name, email, phone, role, active, approval_status')
      .eq('id', userId)
      .maybeSingle();
    if (error) {
      console.error('Could not load profile:', error.message);
      setProfile(null);
      return null;
    }
    setProfile(data);
    return data;
  }, []);

  useEffect(() => {
    let active = true;

    (async () => {
      const { data } = await supabase.auth.getSession();
      if (!active) return;
      setSession(data?.session ?? null);
      if (data?.session) await loadProfile(data.session.user.id);
      if (active) setLoading(false);
    })();

    const { data: sub } = supabase.auth.onAuthStateChange(async (_event, next) => {
      setSession(next);
      if (next) {
        await loadProfile(next.user.id);
      } else {
        setProfile(null);
      }
    });

    return () => {
      active = false;
      sub?.subscription?.unsubscribe();
    };
  }, [loadProfile]);

  const signIn = useCallback(
    async (email, password) => {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: String(email).trim(),
        password,
      });
      if (error) throw error;
      await loadProfile(data.user.id);
      return data;
    },
    [loadProfile],
  );

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setProfile(null);
    setSession(null);
  }, []);

  // Ported from js/register.js: create the auth user, then its profile row.
  const signUp = useCallback(
    async ({ fullName, email, password, phone, role }) => {
      const { data, error } = await supabase.auth.signUp({
        email: String(email).trim(),
        password,
        options: { data: { full_name: fullName } },
      });
      if (error) throw error;

      // Clients start working immediately — their gating happens per project
      // (activation), not per account. Everyone else — site agents, project
      // managers — waits in the approval queue an administrator releases,
      // which is what the admin approval queue is for.
      // Only an administrator can create another administrator account.
      const { error: profileError } = await supabase.from('profiles').insert({
        id: data.user.id,
        full_name: fullName,
        email: String(email).trim(),
        phone: phone || null,
        role,
        active: true,
        approval_status: role === 'client' ? 'Approved' : 'Pending',
      });
      if (profileError) throw profileError;
      return data;
    },
    [],
  );

  const sendPasswordReset = useCallback(async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(String(email).trim());
    if (error) throw error;
  }, []);

  const value = {
    session,
    profile,
    user: session?.user ?? null,
    role: profile?.role ?? null,
    loading,
    signIn,
    signOut,
    signUp,
    sendPasswordReset,
    homeForRole: (r) => ROLE_HOME[r] ?? '/',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}